import { Launch } from '@/redux/slices/focusNewScheduleSlice';
import { IncrementalLaunchStepState } from '../AvailsSchedule/NewSchedule/ReviewSchedule/AddIncrementalLaunch/IncrementalLaunchStep';
import { IncrementalLaunchTimingValue } from '../AvailsSchedule/NewSchedule/ReviewSchedule/AddIncrementalLaunch/IncrementalLaunchTimingSection/IncrementalLaunchTimingSection';
import {
  IncrementalLaunchRightsDraftByTab,
  IncrementalLaunchSelectedVersions
} from '../AvailsSchedule/NewSchedule/ReviewSchedule/AddIncrementalLaunch/IncrementalLaunchWorkspace';
import { LaunchType } from '../AvailsSchedule/NewSchedule/ReviewSchedule/IncrementalLaunchDetailsRow';
import { ScheduleAvailType } from '@/redux/slices/slice_types';

type GetIncrementalLaunchDisplayValuesArgs = {
  offsetUnit: string;
  offsetValue: number;
  offsetReleaseTime: string;
  releaseDate: string;
  releaseTime: string;
  isIncrementalLaunch: boolean;
};

const createDefaultIncrementalLaunchTimingDraftValue = (): IncrementalLaunchTimingValue => ({
  offsetUnit: 'Days',
  offsetValue: 0,
  releaseTime: null,
  timezone: 'PT'
});

const createEmptyIncrementalLaunchRightsDraftByTab = (): IncrementalLaunchRightsDraftByTab => ({
  audio: { add: [], remove: [] },
  territories: { add: [], remove: [] },
  timedtext: { add: [], remove: [] }
});

const createEmptyIncrementalLaunchVersions = (): IncrementalLaunchSelectedVersions => ({
  add: [],
  remove: []
});

export const createLaunch = (type: LaunchType): Launch => ({
  id: crypto.randomUUID(),
  type,
  incrementalLaunchTimingValue: createDefaultIncrementalLaunchTimingDraftValue(),
  incrementalLaunchRightsDraftByTab: createEmptyIncrementalLaunchRightsDraftByTab(),
  incrementalLaunchVersions: createEmptyIncrementalLaunchVersions()
});

export const hasRightsChangesForTab = (incrementalLaunchRightsDraftByTab: IncrementalLaunchRightsDraftByTab, tab: ScheduleAvailType): boolean => {
  const tabState = incrementalLaunchRightsDraftByTab[tab];
  return tabState.add.length > 0 || tabState.remove.length > 0;
};

// Returns the current visual state for a rights step based on the selected tab.
export const getRightsStepState = ({
  selectedRightsTab,
  tab
}: {
  selectedRightsTab: ScheduleAvailType;
  tab: ScheduleAvailType;
}): IncrementalLaunchStepState => {
  return selectedRightsTab === tab ? 'active' : 'inactive';
};

const areHoursAndMinutesEqual = (left: Date | null, right: Date): boolean => {
  if (!left) return false;

  return left.getHours() === right.getHours() && left.getMinutes() === right.getMinutes();
};

// Resolves the final release datetime by applying offset and release-time changes.
const getResolvedTimingDate = (incrementalLaunchTimingValue: IncrementalLaunchTimingValue, previousLaunchReleaseTime: Date): Date => {
  const { offsetUnit, offsetValue, releaseTime } = incrementalLaunchTimingValue;

  const result = new Date(previousLaunchReleaseTime);

  if (offsetUnit === 'Hours') {
    result.setTime(result.getTime() + offsetValue * 60 * 60 * 1000);
  } else if (offsetUnit === 'Days') {
    result.setDate(result.getDate() + offsetValue);
  } else if (offsetUnit === 'Weeks') {
    result.setDate(result.getDate() + offsetValue * 7);
  }

  if (offsetUnit !== 'Hours' && releaseTime) {
    result.setHours(releaseTime.getHours(), releaseTime.getMinutes(), 0, 0);
  }

  return result;
};

export const hasTimingChanges = (incrementalLaunchTimingValue: IncrementalLaunchTimingValue, previousLaunchReleaseTime: Date): boolean => {
  const { offsetUnit, offsetValue, releaseTime } = incrementalLaunchTimingValue;

  if (offsetValue > 0) {
    return true;
  }

  if ((offsetUnit === 'Days' || offsetUnit === 'Weeks') && releaseTime) {
    return !areHoursAndMinutesEqual(releaseTime, previousLaunchReleaseTime);
  }

  return false;
};

export const getReleaseTimingStepCompleted = ({
  incrementalLaunchTimingValue,
  previousLaunchReleaseTime,
  isEditing
}: {
  incrementalLaunchTimingValue: IncrementalLaunchTimingValue;
  previousLaunchReleaseTime: Date;
  isEditing?: boolean;
}): boolean => {
  if (isEditing) {
    return true;
  }

  return hasTimingChanges(incrementalLaunchTimingValue, previousLaunchReleaseTime);
};

// Resolves the first occurrence for the workspace preview.
// This always applies the current timing on top of the previous launch's resolved release time.
export const getFormattedFirstOccurrence = ({
  previousLaunchReleaseTime,
  timing
}: {
  previousLaunchReleaseTime: Date;
  timing: IncrementalLaunchTimingValue;
}): string => {
  const result = getResolvedTimingDate(timing, previousLaunchReleaseTime);

  return result.toLocaleString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  });
};

export const getAlphIdEpisodeLabel = (selectedVersion: any): string => {
  const episodeNumber = selectedVersion.lineage_meta?.episode?.sequence_order;
  const alphaId = selectedVersion.alpha_id;

  return [episodeNumber ? `E${episodeNumber}` : null, alphaId].filter(Boolean).join(': ');
};

// Returns the display strings used for incremental launch summary date/time text.
export const getIncrementalLaunchDisplayValues = ({
  offsetUnit,
  offsetValue,
  offsetReleaseTime,
  releaseDate,
  releaseTime,
  isIncrementalLaunch
}: GetIncrementalLaunchDisplayValuesArgs) => {
  if (!isIncrementalLaunch) {
    return {
      date: releaseDate,
      time: releaseTime
    };
  }

  const baseLabel = 'Previous Launch';

  const unitLabelMap: Record<string, string> = {
    Hours: offsetValue === 1 ? 'hour' : 'hours',
    Days: offsetValue === 1 ? 'day' : 'days',
    Weeks: offsetValue === 1 ? 'week' : 'weeks'
  };

  // NATALIA: add typing enum to offset unit when API are defined
  const offsetLabel = `+ ${offsetValue} ${unitLabelMap[offsetUnit]}`;
  const hasOffset = offsetValue > 0;
  const isHoursOffset = offsetUnit === 'Hours';
  const displayTime = offsetReleaseTime || releaseTime;

  if (!hasOffset) {
    return {
      date: baseLabel,
      time: displayTime
    };
  }

  return {
    date: `${baseLabel} ${offsetLabel}`,
    time: isHoursOffset ? '' : displayTime
  };
};

// Returns the current user's local timezone abbreviation for display, i.e. PT.
export const getUserTimezoneAbbreviation = (): string =>
  new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value || 'UTC';
